import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress, IconButton, InputAdornment, Fade, Slide } from '@mui/material';
import { Visibility, VisibilityOff, LockReset, CheckCircleOutline } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetPasswordApi } from '../Api/AuthPermissionsApi';
import loginImage from '../Assets/Images/login.png';

const ResetPassword = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenParam = queryParams.get('token');

        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setError('Invalid password reset link. Please request a new one.');
        }
    }, [location]);

    const handleReset = async (e) => {
        e.preventDefault();
        if (!token) {
            setError('Missing token.');
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(newPassword)) {
            setError('Password must include uppercase, lowercase, number & special char, min 8 characters.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            await resetPasswordApi({ token, newPassword });
            setSuccess('Password reset successfully. You will be redirected to login.');
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: '#F8F9FA' }}>
            {/* Left Side - Image with decorative background */}
            <Box sx={{ 
                flex: 1, 
                display: { xs: 'none', md: 'flex' }, 
                flexDirection: 'column', 
                background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative blob */}
                <Box sx={{
                    position: 'absolute', top: -100, left: -100,
                    width: 400, height: 400, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(107, 49, 196, 0.1) 0%, rgba(107, 49, 196, 0) 70%)',
                    zIndex: 0
                }} />
                <Box sx={{ p: 4, zIndex: 1 }}>
                    <Typography variant="h5" sx={{ color: '#000', fontWeight: 800, letterSpacing: '-0.5px' }}>
                        Rono <span style={{ color: '#6B31C4' }}>Jobs</span>
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, zIndex: 1 }}>
                    <img src={loginImage} alt="Reset Password" style={{ maxWidth: '90%', maxHeight: '600px', objectFit: 'contain', filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.1))' }} />
                </Box>
            </Box>

            {/* Right Side - Reset Password Form */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 4 }, position: 'relative' }}>
                <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={500}>
                    <Paper elevation={24} sx={{ 
                        p: { xs: 4, sm: 6 }, 
                        width: '100%', 
                        maxWidth: '480px', 
                        borderRadius: '24px', 
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 10px 15px -3px rgba(0, 0, 0, 0.05)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Header Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                            <Box sx={{ 
                                width: 64, height: 64, borderRadius: '20px', 
                                background: 'linear-gradient(135deg, #6B31C4 0%, #9D4EDD 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                                boxShadow: '0 10px 20px rgba(107, 49, 196, 0.3)'
                            }}>
                                <LockReset sx={{ color: '#fff', fontSize: 32 }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827', textAlign: 'center', letterSpacing: '-0.5px' }}>
                                Secure Your Account
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#6B7280', textAlign: 'center', mt: 1 }}>
                                Create a strong new password below.
                            </Typography>
                        </Box>

                        <Fade in={!!error}><Box>{error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}</Box></Fade>
                        <Fade in={!!success}><Box>{success && <Alert severity="success" icon={<CheckCircleOutline />} sx={{ mb: 3, borderRadius: '12px' }}>{success}</Alert>}</Box></Fade>

                        <form onSubmit={handleReset}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>New Password</Typography>
                            <TextField 
                                fullWidth 
                                type={showNewPassword ? 'text' : 'password'} 
                                placeholder="Enter New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={loading || !!success || !token}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: '12px',
                                        backgroundColor: '#F9FAFB',
                                        transition: 'all 0.2s',
                                        '&:hover': { backgroundColor: '#F3F4F6' },
                                        '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 4px rgba(107, 49, 196, 0.1)' }
                                    } 
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: '#9CA3AF', '&:hover': { color: '#6B31C4' } }}>
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>Confirm Password</Typography>
                            <TextField 
                                fullWidth 
                                type={showConfirmPassword ? 'text' : 'password'} 
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading || !!success || !token}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: '12px',
                                        backgroundColor: '#F9FAFB',
                                        transition: 'all 0.2s',
                                        '&:hover': { backgroundColor: '#F3F4F6' },
                                        '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 4px rgba(107, 49, 196, 0.1)' }
                                    } 
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: '#9CA3AF', '&:hover': { color: '#6B31C4' } }}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Button 
                            type="submit" 
                            fullWidth 
                            variant="contained" 
                            disabled={loading || !!success || !token}
                            sx={{ 
                                py: 1.8, 
                                borderRadius: '12px', 
                                backgroundColor: '#6B31C4', 
                                textTransform: 'none',
                                fontSize: '16px',
                                fontWeight: 600,
                                boxShadow: '0 10px 15px -3px rgba(107, 49, 196, 0.3), 0 4px 6px -2px rgba(107, 49, 196, 0.1)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': { 
                                    backgroundColor: '#5A28A8',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 20px 25px -5px rgba(107, 49, 196, 0.4), 0 10px 10px -5px rgba(107, 49, 196, 0.2)'
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={26} color="inherit" /> : 'Update Password'}
                        </Button>
                    </form>
                </Paper>
                </Slide>
            </Box>
        </Box>
    );
};

export default ResetPassword;
